/**
 * Vibeship DevTools Configuration System
 * 
 * A comprehensive configuration system with:
 * - TypeScript support and autocomplete
 * - Schema validation with Zod
 * - Environment variable overrides
 * - Multiple configuration file formats
 * - Configuration merging and defaults
 * - Runtime configuration updates
 * - Migration system for backwards compatibility
 */

// Types
export type {
  VibeshipConfig,
  PartialVibeshipConfig,
  DeepPartial,
  DefineConfigFunction,
  FeatureFlags,
  SecurityConfig,
  AuthConfig,
  AuthProvider,
  RateLimitConfig,
  CORSConfig,
  TaskPatterns,
  CustomTaskPattern,
  UIConfig,
  APIConfig,
  CacheConfig,
  ExperimentalConfig,
  EnvConfig,
} from './types';

// Default configurations
export {
  defaultConfig,
  developmentConfig,
  productionConfig,
  testConfig,
  getEnvironmentDefaults,
} from './defaults';

// Schema validation
export {
  VibeshipConfigSchema,
  PartialVibeshipConfigSchema,
  validateConfig,
  validatePartialConfig,
  isValidConfig,
} from './schema';
export type { VibeshipConfigFromSchema } from './schema';

// Configuration loading and management
export {
  loadConfig,
  defineConfig,
  getConfig,
  clearConfigCache,
  watchConfig,
} from './loader';
export type { LoadConfigOptions } from './loader';

// Configuration merging utilities
export {
  deepMerge,
  mergeConfigurations,
  applyEnvironmentOverrides,
  freezeConfig,
  createConfigPatch,
  isPatchSafe,
} from './merger';

// Migration system
export {
  migrateConfig,
  needsMigration,
  getAvailableMigrations,
  createConfigBackup,
  interactiveMigration,
  rollbackConfig,
  cleanupBackups,
} from './migration';
export type { ConfigMigration, MigrationResult } from './migration';

// Convenience re-exports for common use cases
export { z } from 'zod';

/**
 * Create a configuration with TypeScript support and validation
 * 
 * @example
 * ```typescript
 * import { defineConfig } from '@vibeship/devtools/config';
 * 
 * export default defineConfig({
 *   scanPaths: ['./docs', './README.md'],
 *   features: {
 *     ai: true,
 *     fileEditing: false,
 *   },
 *   security: {
 *     authentication: true,
 *     rateLimitConfig: {
 *       windowMs: 60000,
 *       maxRequests: 100,
 *     },
 *   },
 * });
 * ```
 */